/**
 * La tabla de tramos: qué se midió en cada uno y por qué disparó o no.
 *
 * La columna que importa es la última. «Cero episodios» no es información;
 * «cuarenta maniobras se quedaron a 0,42 m/s² de un umbral de 0,5» sí lo es, y
 * es lo que permite mover la perilla sabiendo qué se va a mover.
 *
 * Se pinta un tope de filas a propósito. Quince mil `<tr>` congelan el
 * navegador y nadie las lee: el filtro y el orden son lo que sirve.
 */

import { useMemo, useState } from "react";
import type { InspectedPair, InspectedSegment } from "@core/pipeline/driving-analysis-v5.ts";

const MAX_ROWS = 250;

type Filter = "disparan" | "cerca" | "todos";

const num = (value: number | null, digits = 2) =>
  value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: digits });

const time = (ms: number | null) =>
  ms === null
    ? "—"
    : new Date(ms).toLocaleString("es-CO", {
        timeZone: "America/Bogota",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

const LABEL: Record<string, string> = {
  acceleration: "Aceleración",
  braking: "Frenada",
  left_turn: "Giro izq.",
  right_turn: "Giro der.",
  elevated_speed: "Velocidad alta",
};

export function SegmentTable({
  pairs,
  segments,
  accelerationThreshold,
  brakingThreshold,
  yawThreshold,
}: {
  pairs: InspectedPair[];
  segments: InspectedSegment[];
  accelerationThreshold: number;
  brakingThreshold: number;
  yawThreshold: number;
}) {
  const [filter, setFilter] = useState<Filter>("disparan");
  const [family, setFamily] = useState<"todas" | "longitudinal" | "turn" | "elevated">("todas");

  const rows = useMemo(() => {
    // «Cerca» es lo que quedó dentro del 30 % por debajo del umbral: son las
    // maniobras que cambiarían de lado si se mueve la perilla.
    const nearAccel = accelerationThreshold * 0.7;
    const nearBrake = brakingThreshold * 0.7;
    const nearYaw = yawThreshold * 0.7;

    const fromPairs = (family === "elevated" ? [] : pairs)
      .map((pair) => {
        const triggeredLong = family !== "turn" && pair.longitudinal.triggered;
        const triggeredTurn = family !== "longitudinal" && pair.turn.triggered;
        const accel = pair.accelerationMps2;
        const yaw = pair.yawRateDegreesPerSecond;
        const near =
          (family !== "turn" &&
            accel !== null &&
            ((accel > 0 && accel >= nearAccel && accel < accelerationThreshold) ||
              (accel < 0 && accel <= nearBrake && accel > brakingThreshold))) ||
          (family !== "longitudinal" &&
            yaw !== null &&
            Math.abs(yaw) >= nearYaw &&
            Math.abs(yaw) < yawThreshold &&
            pair.chained);
        return { pair, triggered: triggeredLong || triggeredTurn, near };
      })
      .filter((row) => (filter === "todos" ? true : filter === "disparan" ? row.triggered : row.near))
      .map((row) => ({
        key: `p${row.pair.index}`,
        atMs: row.pair.atMs,
        kind:
          row.pair.longitudinal.type && family !== "turn"
            ? LABEL[row.pair.longitudinal.type]!
            : row.pair.turn.type
              ? LABEL[row.pair.turn.type]!
              : "—",
        triggered: row.triggered,
        speed: `${num(row.pair.speedFromKmh, 1)} → ${num(row.pair.speedToKmh, 1)}`,
        delta: row.pair.deltaSpeedKmh,
        accel: row.pair.accelerationMps2,
        yaw: row.pair.yawRateDegreesPerSecond,
        heading: row.pair.headingChangeDegrees,
        gap: row.pair.midpointGapSeconds,
        verdict:
          (family !== "turn" && row.pair.longitudinal.triggered) ||
          (family !== "longitudinal" && row.pair.turn.triggered)
            ? null
            : (row.pair.longitudinal.blockedBy ?? row.pair.turn.blockedBy),
      }));

    const fromSegments =
      family === "elevated" || family === "todas"
        ? segments
            .filter((segment) =>
              filter === "todos"
                ? true
                : filter === "disparan"
                  ? segment.elevatedSpeed.triggered
                  : false,
            )
            .map((segment) => ({
              key: `s${segment.index}`,
              atMs: segment.startMs,
              kind: LABEL.elevated_speed!,
              triggered: segment.elevatedSpeed.triggered,
              speed: `${num(segment.implicitSpeedKmh, 1)} km/h`,
              delta: null as number | null,
              accel: null as number | null,
              yaw: null as number | null,
              heading: null as number | null,
              gap: segment.elapsedSeconds,
              verdict: segment.elevatedSpeed.triggered ? null : segment.elevatedSpeed.blockedBy,
            }))
        : [];

    const all = family === "elevated" ? fromSegments : [...fromPairs, ...fromSegments];
    return all.sort((a, b) => (a.atMs ?? 0) - (b.atMs ?? 0));
  }, [pairs, segments, filter, family, accelerationThreshold, brakingThreshold, yawThreshold]);

  const shown = rows.slice(0, MAX_ROWS);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-md border border-zinc-300 p-0.5 dark:border-zinc-700">
          {(
            [
              ["disparan", "Los que disparan"],
              ["cerca", "Los que quedaron cerca"],
              ["todos", "Todos"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`rounded px-2 py-1 text-xs ${
                filter === value
                  ? "bg-brand-600 text-white"
                  : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <select
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          value={family}
          onChange={(event) => setFamily(event.target.value as typeof family)}
        >
          <option value="todas">Todas las familias</option>
          <option value="longitudinal">Aceleración y frenada</option>
          <option value="turn">Giros</option>
          <option value="elevated">Velocidad elevada</option>
        </select>

        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {rows.length.toLocaleString("es-CO")} tramo(s)
          {rows.length > MAX_ROWS ? ` · se muestran los primeros ${MAX_ROWS}` : ""}
        </span>
      </div>

      {filter === "cerca" ? (
        <p className="mb-2 text-xs leading-snug text-zinc-500 dark:text-zinc-400">
          Maniobras que quedaron dentro del 30 % por debajo del umbral: son las que cambiarían de lado
          si mueves la perilla. Si aquí hay muchas, el umbral está partiendo una nube de datos por la
          mitad y conviene mirarlo.
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
              <th className="py-1.5 pr-2 font-medium">Instante</th>
              <th className="py-1.5 pr-2 font-medium">Qué</th>
              <th className="py-1.5 pr-2 font-medium">Velocidad</th>
              <th className="py-1.5 pr-2 text-right font-medium">Δ km/h</th>
              <th className="py-1.5 pr-2 text-right font-medium">m/s²</th>
              <th className="py-1.5 pr-2 text-right font-medium">rumbo °</th>
              <th className="py-1.5 pr-2 text-right font-medium">°/s</th>
              <th className="py-1.5 pr-2 text-right font-medium">Δt s</th>
              <th className="py-1.5 pr-2 font-medium">Veredicto</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr
                key={row.key}
                className={`border-b border-zinc-100 last:border-0 dark:border-zinc-800/60 ${
                  row.triggered ? "bg-amber-50/60 dark:bg-amber-950/20" : ""
                }`}
              >
                <td className="py-1 pr-2 whitespace-nowrap text-xs tabular-nums text-zinc-500">
                  {time(row.atMs)}
                </td>
                <td className="py-1 pr-2 whitespace-nowrap">
                  {row.triggered ? (
                    <span className="font-medium text-amber-800 dark:text-amber-300">{row.kind}</span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-1 pr-2 whitespace-nowrap tabular-nums text-zinc-600 dark:text-zinc-400">
                  {row.speed}
                </td>
                <td className="py-1 pr-2 text-right tabular-nums">{num(row.delta, 1)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{num(row.accel, 3)}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-zinc-500">{num(row.heading, 1)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{num(row.yaw, 2)}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-zinc-500">{num(row.gap, 1)}</td>
                <td className="py-1 pr-2 text-xs leading-snug text-zinc-500 dark:text-zinc-400">
                  {row.verdict ?? <span className="text-amber-700 dark:text-amber-400">disparó</span>}
                </td>
              </tr>
            ))}
            {!shown.length ? (
              <tr>
                <td colSpan={9} className="py-4 text-center text-sm text-zinc-500">
                  Ningún tramo cumple ese filtro con los umbrales actuales.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
